import { Card, Row, Col, Tabs, Spin } from 'antd';
import TabPane from 'antd/lib/tabs/TabPane';
import React, { FC, useRef, useState } from 'react';
import clsx from 'clsx';
import ScopeOneViewComponent from '../ScopeOneViewComponent';
import styles from '../activityDataEntry.module.scss';
import { ScopeData } from 'models/ActivityDataEntryScopes/scopes.model';
import { Scope1DesiredData } from 'models/ActivityDataScope1DesiredData/scope1DesiredData.model';
import { Scope2DesiredData } from 'models/ActivityDataScope2DesiredData/scope2DesiredData.model';
import { ProductionData } from 'models/ProductionData/productionData.model';
import { FormikProps } from 'formik';
import { LocationData } from 'models/ActivityDataEntryScopes/locationData.model';
import useFilters from 'shared/hooks/useFilters';
import { ScopeThreeData } from 'models/ActivityDataEntryScopes/scopeThree.model';
import { queryParamsList } from '..';
import ScopeTwoFormComponent from '../ScopeTwoViewComponent';
import ProductionDataComponent from '../ProductionDataComponent';
import UploadModal from '../UploadModal';
import { useParams } from 'react-router-dom';
import ActivityDataEntryService from 'services/ActivityDataEntryService';
import { DownloadLoader } from 'enums/GraphDropdownEnums';
import { Location } from 'models/ActivityDataEntryLocation/location.model';
import { IncompleteIcon } from '../activityDataEntry.util';
import ScopeThreeUpStreamList from '../ScopeUpstreamList';
import ScopeThreeDownStreamList from '../ScopeDownstreamList';
import { FarmPageProps, commodityDropdown } from 'enums/FarmPageEnum';
import { ReactComponent as AgricultureIcon } from 'assets/icons/agriculture.svg';
import { ReactComponent as LiveStockIcon } from 'assets/icons/livestock.svg';
import CommodityActivityDataEntryList from 'views/commodityActivityDataEntryList';
import ValidationModal from 'shared/components/ScopeValidationModal';
import useRedirect from 'shared/utils/useRedirect';
import CancelFormModal from './CancelFormModal';
import { generateTemplateButtons } from './templateButtonsUtils';
import { useUserRole } from 'shared/hooks/useUserRole';
import { isViewer, isDataContributor, canCreate } from 'shared/utils/roleUtils';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';

interface BaseYearChildrenComponentProps {
  activeScope: string;
  setActiveScope: React.Dispatch<React.SetStateAction<string>>;
  setIsEditView: React.Dispatch<React.SetStateAction<boolean>>;
  isEditView: boolean;
  scope1Details?: Scope1DesiredData;
  scope2Details?: Scope2DesiredData;
  productionData?: ProductionData;
  activeYear?: string;
  locationScopeDetails?: LocationData;
  fetchYearForLocation?: (
    locationId?: string | undefined,
    year?: string | undefined
  ) => Promise<void>;
  createDesiredScopeData?: (
    locationScopeDetails?: LocationData | undefined
  ) => void;
  createScopeForLocation?: (
    locationId?: string | undefined,
    year?: string | undefined,
    scopeCategory?: string | undefined,
    payload?: ScopeData | undefined
  ) => Promise<void>;
  updateProductionData?: (
    locationId?: string,
    year?: string,
    payload?: ProductionData | undefined
  ) => Promise<void>;
  scopeLoading?: boolean;
  setIsAlert?: React.Dispatch<React.SetStateAction<boolean>>;
  isEditViewComponent?: boolean;
  scopeThreeData?: ScopeThreeData;
  location?: Location;
}

const BaseYearChildrenComponent: FC<BaseYearChildrenComponentProps> = props => {
  const {
    activeScope,
    setActiveScope,
    setIsEditView,
    isEditView,
    scope1Details,
    scope2Details,
    productionData,
    activeYear,
    locationScopeDetails,
    fetchYearForLocation,
    createDesiredScopeData,
    createScopeForLocation,
    updateProductionData,
    scopeLoading,
    setIsAlert,
    isEditViewComponent,
    location,
  } = props;
  const { id: locationId } = useParams();
  const { redirectToRespectiveScopeTab } = useRedirect();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const { downloadScopeTemplate, loading } = ActivityDataEntryService();
  const form1Ref = useRef<FormikProps<Scope1DesiredData>>(null);
  const form2Ref = useRef<FormikProps<Scope2DesiredData>>(null);
  const productionDataRef = useRef<FormikProps<ProductionData>>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isValidationModalOpen, setIsValidationModalOpen] =
    useState<boolean>(false);
  const [isFarmCommodityModalOpen, setIsFarmCommodityModalOpen] =
    useState<boolean>(false);
  const [isLiveStockCommodityModalOpen, setIsLiveStockCommodityModalOpen] =
    useState<boolean>(false);

  const { getParams, setParams } = useFilters();
  const { activeTab, isFirstFlow } = getParams(queryParamsList);
  const userRole = useUserRole();
  const isViewerRole = isViewer(userRole);
  const isDataContributorRole = isDataContributor(userRole);
  const canCreateCommodity = canCreate(userRole);
  const isFarmCategory: boolean =
    location?.locationCategory === FarmPageProps.PAGE_TITLE;

  const handleSave = async () => {
    if (isViewerRole) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    if (activeScope === 'scope1' && form1Ref.current) {
      form1Ref.current.submitForm();
    } else if (activeScope === 'scope2' && form2Ref.current) {
      form2Ref.current.submitForm();
    } else if (activeScope === 'productionData' && productionDataRef.current) {
      productionDataRef.current.submitForm();
    }
  };
  const toggleUploadModal = () => setUploadModalVisible(prev => !prev);
  const closeScopeValidationModal = () => setIsValidationModalOpen(false);

  const handleCancelClick = () => setIsCancelModalOpen(true);
  const handleCancelModalClose = () => setIsCancelModalOpen(false);
  const handleCancelOk = async () => {
    setIsEditView(false);
    setParams(
      isFirstFlow
        ? { activeTab: activeYear, isFirstFlow: true }
        : { activeTab: activeYear },
      undefined,
      true
    );
    await fetchYearForLocation?.(locationId, activeYear);
    setIsCancelModalOpen(false);
  };

  const handleEdit = () => {
    if (isViewerRole) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    setIsEditView(true);
    setParams(
      isFirstFlow
        ? { activeTab: activeYear, isEditView: true, isFirstFlow: true }
        : { activeTab: activeYear, isEditView: true },
      undefined,
      true
    );
  };

  const downloadTemplate = async () => {
    if (!locationId) return;
    await downloadScopeTemplate(locationId, activeTab, activeScope);
  };
  const { scopeCompletionStatus } = locationScopeDetails || {};
  const { scope1Status, scope2Status, revenueStatus } = scopeCompletionStatus || {};

  const handleAgricultureCommodity = () => {
    if (!canCreateCommodity) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    const isStatusComplete = scope1Status && scope2Status;
    if (isStatusComplete) setIsFarmCommodityModalOpen(true);
    else setIsValidationModalOpen(!isStatusComplete);
  };
  const handleLiveStockCommodity = () => {
    if (!canCreateCommodity) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    const isStatusComplete = scope1Status && scope2Status;
    if (isStatusComplete) setIsLiveStockCommodityModalOpen(true);
    else setIsValidationModalOpen(!isStatusComplete);
  };

  const handleTabChange = (key: string) => {
    {
      if (isEditView) setIsAlert?.(true);
      else setActiveScope(key);
      redirectToRespectiveScopeTab(locationId, key, activeYear);
    }
  };
  const commodityDropdownItems = [
    {
      value: commodityDropdown.AGRICULTURE,
      icon: <AgricultureIcon />,
      onClick: () => handleAgricultureCommodity(),
      disabled: !canCreateCommodity,
    },
    {
      value: commodityDropdown.LIVE_STOCK,
      icon: <LiveStockIcon />,
      onClick: () => handleLiveStockCommodity(),
      disabled: !canCreateCommodity,
    },
  ];

  const templateButtons = generateTemplateButtons({
    isFarmCategory,
    isEditView,
    downloadTemplate,
    toggleUploadModal,
    handleCancelClick,
    handleSave,
    activeScope,
    handleEdit,
    commodityDropdownItems,
    loading: loading || scopeLoading,
    isViewerRole,
  });

  const getScopeLabel = (scopeKey: number) => {
    const scopeStatusMap = {
      1: scope1Status,
      2: scope2Status,
    } as { [key: number]: boolean };
    const isInComplete = !scopeStatusMap[scopeKey];
    return (
      <Row align="middle">
        <Col>Scope {scopeKey}</Col>
        {isInComplete && (
          <Col className={styles.scopeAlertIcon}>{<IncompleteIcon />}</Col>
        )}
      </Row>
    );
  };

  const getProductionDataLabel = () => {
    const isInComplete = !revenueStatus;
    return (
      <Row align="middle">
        <Col>Intensity Data</Col>
        {isInComplete && (
          <Col className={styles.scopeAlertIcon}>{<IncompleteIcon />}</Col>
        )}
      </Row>
    );
  };

  return (
    <Card className={styles.cardContainer}>
      <div className={styles.cardWrapper}>
        <Spin
          className={styles.scopeLoadingContainer}
          spinning={scopeLoading || loading}
          tip={loading && DownloadLoader.DOWNLOADING}>
          <div className={styles.actionContainer}>
            <Row className={styles.tabsWrapper}>
              <Col span={24}>
                <Tabs
                  activeKey={activeScope}
                  onChange={handleTabChange}
                  className={clsx(styles.scopeTabs, {
                    [styles.editMode]: isEditView,
                  })}
                  tabBarExtraContent={templateButtons}>
                  <TabPane tab={getProductionDataLabel()} key="productionData">
                    <ProductionDataComponent
                      productionData={productionData}
                      formikRef={productionDataRef}
                      activeScope={activeScope}
                      activeYear={activeYear}
                      locationId={locationId}
                      updateProductionData={updateProductionData}
                      fetchYearForLocation={fetchYearForLocation}
                      setIsEdit={setIsEditView}
                      isEditViewComponent={isEditViewComponent}
                    />
                  </TabPane>
                  <TabPane tab={getScopeLabel(1)} key="scope1">
                    <ScopeOneViewComponent
                      scope1Details={scope1Details}
                      formikRef={form1Ref}
                      activeScope={activeScope}
                      activeYear={activeYear}
                      locationScopeDetails={locationScopeDetails}
                      createScopeForLocation={createScopeForLocation}
                      fetchYearForLocation={fetchYearForLocation}
                      createDesiredScopeData={createDesiredScopeData}
                      setIsEdit={setIsEditView}
                    />
                  </TabPane>
                  <TabPane tab={getScopeLabel(2)} key="scope2">
                    <ScopeTwoFormComponent
                      scope2Details={scope2Details}
                      formikRef={form2Ref}
                      activeScope={activeScope}
                      activeYear={activeYear}
                      locationScopeDetails={locationScopeDetails}
                      createScopeForLocation={createScopeForLocation}
                      fetchYearForLocation={fetchYearForLocation}
                      createDesiredScopeData={createDesiredScopeData}
                      setIsEdit={setIsEditView}
                      isEditViewComponent={isEditViewComponent}
                    />
                  </TabPane>
                  <TabPane tab="Scope 3 Upstream" key="scope3Upstream">
                    <ScopeThreeUpStreamList
                      locationCategory={location?.locationCategory}
                    />
                  </TabPane>
                  <TabPane tab="Scope 3 Downstream" key="scope3Downstream">
                    <ScopeThreeDownStreamList />
                  </TabPane>

                  {isFarmCategory && (
                    <TabPane tab="Commodity" key="commodity">
                      <CommodityActivityDataEntryList
                        locationScopeDetails={locationScopeDetails}
                        setModalStatus={setIsValidationModalOpen}
                        setIsFarmCommodityModal={setIsFarmCommodityModalOpen}
                        isFarmCommodityModal={isFarmCommodityModalOpen}
                        modalStatus={isValidationModalOpen}
                        activeYear={activeYear}
                        locationId={locationId}
                        location={location}
                        setIsLiveStockCommodityModal={
                          setIsLiveStockCommodityModalOpen
                        }
                        isLiveStockCommodityModal={
                          isLiveStockCommodityModalOpen
                        }
                      />
                    </TabPane>
                  )}
                </Tabs>
              </Col>
              <CancelFormModal
                visible={isCancelModalOpen}
                onClose={handleCancelModalClose}
                onConfirm={handleCancelOk}
              />
              <UploadModal
                visible={uploadModalVisible}
                onClose={toggleUploadModal}
                activeScope={activeScope}
                successCallback={handleCancelOk}
              />
              <ValidationModal
                modalStatus={isValidationModalOpen}
                closeModal={closeScopeValidationModal}
              />
            </Row>
          </div>
        </Spin>
      </div>
    </Card>
  );
};

export default BaseYearChildrenComponent;
